import { ThunkDispatch } from '@reduxjs/toolkit';
import React from 'react';
import { connect, MapStateToProps, MapDispatchToProps } from 'react-redux';
import { Helmet } from 'react-helmet';
import { RouteComponentProps } from '@reach/router';
import { ITasks, RootState } from 'store/store';
import { Section, Container, Box, TabsList, TabItem } from './styles';
import MsgView from './MsgView';
import messages from './Messages';
import LogoLoading from 'components/LogoLoading';
import { display } from 'components/ErrorView/ErrorDialog';
import InfiniteList from 'components/InfiniteList';
import TaskEditorCont from '../TasksManagement/TaskEditorCont';
import { IThisUserState } from 'store/thisUser/thisUserSlice';
import { ICompanyState } from 'store/companies/companiesTypes';
import {
  archiveMessages,
  archiveMessagesFn,
  IMessageState,
  markAsRead,
  markAsReadFn,
} from 'store/messages/messagesSlice';
import {
  IConversation,
  loadOlderMessages,
  loadOlderMessagesFn,
  requireConversation,
  requireConversationFn,
} from 'store/conversation/conversationSlice';
import { getTask, getTaskFn } from 'store/tasks/tasksSlice';

interface IStateToProps {
  thisUser: IThisUserState;
  companies: ICompanyState;
  tasks: ITasks;
  conversation: IConversation;
  message: IMessageState;
}

interface IDispatchToProps {
  requireConversation: (
    conversationId: number
  ) => ReturnType<typeof requireConversationFn>;
  loadOlderMessages: (
    conversationId: number,
    count?: number
  ) => ReturnType<typeof loadOlderMessagesFn>;
  getTask: (taskId: number) => ReturnType<typeof getTaskFn>;
  markAsRead: (conversationId: number) => ReturnType<typeof markAsReadFn>;
  archiveMessages: (
    messagesIds: number[],
    archive: boolean
  ) => ReturnType<typeof archiveMessagesFn>;
}

interface IOwnProps {
  companyId?: string;
}

type IComponentProps = IOwnProps &
  IStateToProps &
  IDispatchToProps &
  RouteComponentProps;

interface IComponentStates {
  showTask?: number;
  showArchived: boolean;
  lastSeen: number;
}

class InboxContainer extends React.PureComponent<
  IComponentProps,
  IComponentStates
> {
  constructor(props: IComponentProps) {
    super(props);

    this.state = {
      showArchived: false,
      lastSeen: 1,
    };
  }

  async componentDidMount() {
    const companyId = Number(this.props.companyId);
    const userId = Number(this.props.thisUser.userId);
    const convId =
      this.props.companies?.data?.[companyId]?.members?.[userId]
        ?.conversationInboxId;

    this.props.requireConversation(convId).then(
      () => {
        this.setState({
          lastSeen: this.props.conversation.myLastMessageSeen,
        });
        this.props.markAsRead(convId);
      },
      (err: any) => {
        display(err);
      }
    );

    return Promise.resolve();
  }

  handleLoadMessages = () => {
    return this.props.loadOlderMessages(this.props.conversation.id);
  };

  handleClickOnTask = (taskId: number) => {
    this.props
      .getTask(taskId)
      .then(() => {
        this.setState({ showTask: taskId });
      })
      .catch((err: any) => {
        display(err);
      });
  };

  handleCloseTask = () => {
    this.setState({ showTask: undefined });
  };

  handleArchiveMsg = (msgId: number, archive: boolean = true) => {
    const messagesIds = [msgId];
    this.props.archiveMessages(messagesIds, archive).catch((err: any) => {
      display(err);
    });
  };

  handleShowArchived = (state: boolean) => () => {
    this.setState({ showArchived: state });
  };

  render() {
    const { conversation, message } = this.props;
    if (!conversation) {
      return <LogoLoading />;
    }
    const convMsg = Object.values(message.data).filter(
      (msg) => msg.conversationId === conversation.id
    );
    const messagesToView = convMsg
      .filter((msg) => msg.archived === this.state.showArchived)
      .sort((a, b) => b.sendTime - a.sendTime);
    return (
      <React.Fragment>
        <Helmet>
          <title>{messages.inbox}</title>
          <meta name="description" content={messages.inbox} />
        </Helmet>
        <Section>
          <Container halve={!!this.state.showTask}>
            <Box>
              <TabsList>
                <TabItem
                  onClick={this.handleShowArchived(false)}
                  active={!this.state.showArchived}
                >
                  {messages.new}
                </TabItem>
                <TabItem
                  onClick={this.handleShowArchived(true)}
                  active={this.state.showArchived}
                >
                  {messages.archived}
                </TabItem>
              </TabsList>
            </Box>
            <InfiniteList onLoadMore={this.handleLoadMessages}>
              {convMsg.length === 0 ? (
                <p className="center-vh">{messages.thereIsNoMessages}</p>
              ) : (
                <React.Fragment>
                  {Object.values(messagesToView).map((msg) => (
                    <MsgView
                      selected={msg.id === this.state.showTask}
                      key={msg.id}
                      msg={msg}
                      onClickOnMessage={this.handleClickOnTask}
                      onArchiveMsg={this.handleArchiveMsg}
                      myLastMessageSeen={this.state.lastSeen}
                    />
                  ))}
                </React.Fragment>
              )}
            </InfiniteList>
          </Container>
          {this.state.showTask && (
            <div
              style={{
                flex: '1 1 0%',
                display: 'flex',
                minHeight: '0px',
                flexDirection: 'column',
                backgroundColor: 'rgb(255, 255, 255)',
                minWidth: '500px',
                transition: 'width 1s',
                borderRadius: '15px',
                boxShadow: 'rgba(0, 0, 0, 0.15) 0px 2px 8px',
                marginInlineStart: '0.5em',
                padding: '0px !important',
              }}
            >
              <TaskEditorCont
                companyId={Number(this.props.companyId)}
                onCloseTask={this.handleCloseTask}
                tab="info"
                taskId={this.state.showTask}
              />
            </div>
          )}
        </Section>
      </React.Fragment>
    );
  }
}

const mapStateToProps: MapStateToProps<IStateToProps, IOwnProps, RootState> = (
  state,
  props
) => {
  const thisUser = state.thisUser;
  const companies = state.companies;

  const companyId = Number(props.companyId);
  const userId = Number(thisUser.userId);
  const convId = companies.data[companyId].members[userId].conversationInboxId;
  const conversation = state.conversations.data[convId];

  const message = state.messages;
  const tasks = state.tasks;

  return { thisUser, companies, conversation, message, tasks };
};

const mapDispatchToProps: MapDispatchToProps<IDispatchToProps, IOwnProps> = (
  dispatch: ThunkDispatch<any, never, any>
) => ({
  requireConversation: (conversationId: number) =>
    dispatch(requireConversation({ conversationId })).unwrap(),
  loadOlderMessages: (conversationId: number, count?: number) =>
    dispatch(
      loadOlderMessages({
        conversationId,
        count,
      })
    ).unwrap(),
  getTask: (taskId: number) => dispatch(getTask({ taskId })).unwrap(),
  markAsRead: (conversationId: number) =>
    dispatch(
      markAsRead({
        conversationId,
      })
    ).unwrap(),
  archiveMessages: (messagesIds: number[], archive: boolean) =>
    dispatch(
      archiveMessages({
        messagesIds,
        archive,
      })
    ).unwrap(),
});

export default connect(mapStateToProps, mapDispatchToProps)(InboxContainer);
